// Die Suche im Einstellungsfenster: Text zuerst, Bedeutung dazu.
//
// Der Textvergleich (trifft() im Manifest) findet, was man beim Namen
// nennt. Wer fragt statt nennt ("was kostet der Strom", "wo bin ich
// eingeloggt"), trifft kein Wort -- dafuer rechnet bge-m3 (Inferenzdienst,
// wie in der Suche der Kopfleiste) die Naehe zwischen Frage und Eintrag.
// Diese Treffer stehen UNTER den Texttreffern, als eigene Gruppe: sie
// sind eine Vermutung, kein Fund.
//
// Rein gerechnet, ohne Stores (Tests: lib/einstellungen-suche.test.mjs).

export interface Suchbar {
  id: string;
  name: string;
  kategorie: string;
  info?: string | (() => string);
  such?: string[];
  absicht: string;
}

/** Schluessel eines Eintrags im Embedding-Index (semantic-search). */
export const semKey = (id: string): string => `einst:${id}`;

/** Text, aus dem das Embedding eines Eintrags entsteht. NUR Festes:
 *  ein Untertitel, der vom geladenen Zustand abhaengt (info als
 *  Funktion), wuerde bei jeder Aenderung einen neuen Vektor erzeugen. */
export function suchText(e: Suchbar): string {
  const info = typeof e.info === "string" ? e.info : "";
  return [e.name, e.kategorie, info, (e.such ?? []).join(", "), e.absicht].filter(Boolean).join(". ");
}

// Gemessen am 04.10.2026 gegen die 50 Eintraege (bge-m3, Texte samt
// Absicht): das richtige Ziel liegt fast immer vorn, aber sein Cosinus
// (0,42–0,69) ueberlappt mit dem besten Treffer einer Unsinns-Frage
// ("hallo": 0,48). Ein fester Boden trennt also nicht. Was trennt, ist
// der ABSTAND ZUM MITTEL ueber alle Eintraege: eine echte Frage hebt ihr
// Ziel heraus (15 Umschreibungen: +0,10 bis +0,34, zwoelf davon ab
// +0,16), Unsinn hebt alles gleich wenig (sieben Proben: hoechstens
// +0,11).
/** So weit muss ein Eintrag ueber dem Mittel liegen. */
export const SINN_ABSTAND = 0.13;
/** Nur was nah am besten Treffer liegt -- sonst haengt an jeder Frage
 *  ein Schwanz aus halb Passendem. */
export const SINN_FENSTER = 0.07;
export const SINN_HOECHSTENS = 4;

/**
 * Die Sinn-Treffer zu einer Frage.
 *
 * @param alle     der ganze Bestand -- das Mittel braucht alle, nicht nur
 *                 die gerade gefilterten
 * @param naehe    Cosinus zwischen Frage und Eintrag; undefined, wenn der
 *                 Eintrag (noch) keinen Vektor hat
 * @param zulaessig darf der Eintrag gezeigt werden (Bereich, Kategorie)
 *                 und ist er nicht schon ein Texttreffer?
 */
export function sinnTreffer<T>(
  alle: T[],
  naehe: (x: T) => number | undefined,
  zulaessig: (x: T) => boolean,
): T[] {
  const werte: { x: T; c: number }[] = [];
  for (const x of alle) {
    const c = naehe(x);
    if (typeof c === "number" && Number.isFinite(c)) werte.push({ x, c });
  }
  // Mit einer Handvoll Vektoren ist "das Mittel" keins.
  if (werte.length < 8) return [];
  const mittel = werte.reduce((a, w) => a + w.c, 0) / werte.length;
  const bester = Math.max(...werte.map((w) => w.c));
  return werte
    .filter((w) => w.c - mittel >= SINN_ABSTAND && w.c >= bester - SINN_FENSTER && zulaessig(w.x))
    .sort((a, b) => b.c - a.c)
    .slice(0, SINN_HOECHSTENS)
    .map((w) => w.x);
}

// ── Verweis auf die Kategorie „Eingriffe" ────────────────────────────
// Was die Anmeldung aushebelt, steht nicht in der Trefferliste; wer danach
// sucht, bekommt einen Verweis auf die Kategorie. Bis 05.10.2026 musste
// dafür JEDES Wort der Frage zu den Eingriffs-Worten gehören -- ein ganzer
// Satz ("Anmeldung für lokale Zugriffe abschalten") scheiterte an "für"
// und "abschalten" und landete bei den Passkeys.
const EINGRIFF_WORTE =
  "eingriff eingriffe kritisch bypass lokal lokale zugriffe zugriff anmeldung aushebeln umgehen push-bestätigung push-anmeldung freigeben falscher code sperre wartung".split(
    " ",
  );
/** Füllwörter zählen nicht mit. */
const FUELLWORTE = new Set(
  "aber alle auch beim dass dem den der des die das dies diese doch eine einen einem einer für habe hier ich ist kann man mein meine mich mit nach nicht noch oder ohne sich sind soll über und vom von wann warum was wenn wer wie wird wo zu zum zur".split(
    " ",
  ),
);

/** Meint die Frage einen Eingriff? Ein Wort, das trifft, genügt nur,
 *  wenn es das einzige ist ("bypass"); bei mehreren müssen zwei treffen
 *  -- "Anmeldung ändern" meint die Passkeys, nicht den Bypass. */
export function meintEingriff(frage: string): boolean {
  const q = frage.trim().toLowerCase();
  if (q.length < 4) return false;
  const worte = q.split(/[^\p{L}\p{N}-]+/u).filter((w) => w.length >= 3 && !FUELLWORTE.has(w));
  if (worte.length === 0) return false;
  // Beim Tippen ist das Wort der Frage der Anfang ("bypa"); gebeugt ist es
  // länger als das Stichwort ("lokaler" zu "lokal").
  const treffer = worte.filter((teil) => EINGRIFF_WORTE.some((w) => w.startsWith(teil) || (w.length >= 5 && teil.startsWith(w)))).length;
  return worte.length === 1 ? treffer === 1 : treffer >= 2;
}

